import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { requestsAPI } from '../../services/api';
import { Clock, CheckCircle, XCircle, Loader2, ArrowRight, User, Calendar } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const RequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    loadRequests();
  }, [filter]);

  useEffect(() => {
    loadCounts();
  }, []);

  const loadCounts = async () => {
    try {
      const [p, a, r] = await Promise.all([
        requestsAPI.getAll('pending'),
        requestsAPI.getAll('approved'),
        requestsAPI.getAll('rejected'),
      ]);
      setCounts({ pending: p.data.length, approved: a.data.length, rejected: r.data.length });
    } catch {}
  };

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const response = await requestsAPI.getAll(filter);
      setRequests(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await requestsAPI.approve(id);
      toast.success('Solicitud aprobada');
      loadRequests();
      loadCounts();
    } catch (error) {
      toast.error('Error al aprobar');
    }
  };

  const handleReject = async (id) => {
    try {
      await requestsAPI.reject(id);
      toast.success('Solicitud rechazada');
      loadRequests();
      loadCounts();
    } catch (error) {
      toast.error('Error al rechazar');
    }
  };

  const statusConfig = {
    pending: { badge: 'bg-amber-100 text-amber-700 border border-amber-200', label: 'Pendiente', icon: Clock },
    approved: { badge: 'bg-green-100 text-green-700 border border-green-200', label: 'Aprobada', icon: CheckCircle },
    rejected: { badge: 'bg-red-100 text-red-700 border border-red-200', label: 'Rechazada', icon: XCircle },
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Solicitudes de Cambio
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Gestiona las solicitudes de cambio de día de los colaboradores
            </p>
          </div>
          <div className="flex gap-2">
            {[
              { key: 'pending', label: 'Pendientes' },
              { key: 'approved', label: 'Aprobadas' },
              { key: 'rejected', label: 'Rechazadas' },
            ].map(({ key, label }) => (
              <Button
                key={key}
                variant={filter === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(key)}
                className={`rounded-sm ${filter === key ? 'bg-[#002FA7]' : ''}`}
                data-testid={`filter-${key}`}
              >
                {label} ({counts[key]})
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : requests.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <Clock className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No hay solicitudes {filter === 'pending' ? 'pendientes' : filter === 'approved' ? 'aprobadas' : 'rechazadas'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {requests.map((req) => {
              const sc = statusConfig[req.status] || statusConfig.pending;
              return (
                <Card key={req.id} className="border border-slate-200 hover:shadow-sm transition-shadow" data-testid={`request-${req.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        {/* Header row */}
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-[#002FA7]/10 flex items-center justify-center shrink-0">
                            <User className="w-4 h-4 text-[#002FA7]" />
                          </div>
                          <div>
                            <p className="font-semibold text-base">{req.user_name || 'Colaborador'}</p>
                            <p className="text-xs text-slate-400">{req.user_email}</p>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.badge}`}>
                            {sc.label}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                            {req.request_type === 'change' ? 'Cambio de día' : 'Intercambio'}
                          </span>
                        </div>

                        {/* Day change visual */}
                        <div className="flex items-center gap-3 ml-12">
                          <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-md border border-red-100">
                            <Calendar className="w-3.5 h-3.5 text-red-500" />
                            <div>
                              <p className="text-xs text-red-500 font-medium">{req.original_day}</p>
                              <p className="text-[11px] text-red-400">{req.original_date}</p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                          <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-md border border-green-100">
                            <Calendar className="w-3.5 h-3.5 text-green-600" />
                            <div>
                              <p className="text-xs text-green-600 font-medium">{req.requested_day}</p>
                              <p className="text-[11px] text-green-500">{req.requested_date}</p>
                            </div>
                          </div>
                        </div>

                        {/* Reason */}
                        <div className="ml-12 p-3 bg-slate-50 rounded-md border border-slate-100">
                          <p className="text-sm text-slate-600 leading-relaxed">"{req.reason}"</p>
                        </div>
                      </div>

                      {/* Action buttons */}
                      {req.status === 'pending' && (
                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(req.id)}
                            className="bg-green-600 hover:bg-green-700 rounded-sm h-9 px-4"
                            data-testid={`approve-${req.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1.5" />
                            Aprobar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(req.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50 rounded-sm h-9 px-4"
                            data-testid={`reject-${req.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1.5" />
                            Rechazar
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default RequestsPage;
